import { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Siren, ShieldAlert, Eye, Megaphone, MapPin, Loader2 } from "lucide-react";
import { TransportModal } from "@/components/mdt/TransportModal";

const EXT_STATUSES = [["staged", "STAGED"], ["transporting", "TRANSPORTING"], ["at_destination", "AT DESTINATION"],
  ["report_writing", "REPORT WRITING"], ["busy", "BUSY"], ["meal", "MEAL"], ["court", "COURT"], ["training", "TRAINING"],
  ["special_assignment", "SPECIAL ASSIGN"], ["out_of_service", "OUT OF SERVICE"], ["emergency_traffic", "EMERG TRAFFIC"], ["available", "AVAILABLE"]];
const SAFETY = [["officer_needs_assistance", "OFFICER NEEDS ASSISTANCE", "#EF4444"], ["emergency_traffic", "EMERGENCY TRAFFIC", "#EF4444"],
  ["request_backup", "IMMEDIATE BACKUP", "#F59E0B"], ["request_ems", "EMS FOR RESPONDER", "#F59E0B"],
  ["weapon", "WEAPON", "#EF4444"], ["active_threat", "ACTIVE THREAT", "#EF4444"],
  ["foot_pursuit", "FOOT PURSUIT", "#F59E0B"], ["vehicle_pursuit", "VEHICLE PURSUIT", "#F59E0B"],
  ["use_of_force", "USE OF FORCE", "#A855F7"], ["injury", "INJURY", "#F59E0B"],
  ["hazard", "HAZARD", "#22D3EE"], ["send_location", "SEND MY LOCATION", "#10B981"]];

const gps = () => new Promise((res) => {
  if (!navigator.geolocation) return res({});
  navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res({}), { timeout: 4000 });
});

export function MdtStatusStrip({ myUnit, unit, onChanged, offline = false, enqueueStatus, activeCall }) {
  const [busy, setBusy] = useState("");
  const [tModal, setTModal] = useState(null);
  const send = async (s, label, extra = {}) => {
    setBusy(s);
    try {
      const loc = await gps();
      await client.patch(`/units/${myUnit}/status`, { status: s, ...extra, ...loc });
      toast.success(`Status: ${label}`); onChanged?.();
    } catch (e) {
      if (!e.response && enqueueStatus) enqueueStatus(s, label);
      else { toast.error(e.response?.data?.detail || "Status change failed"); throw e; }
    } finally { setBusy(""); }
  };
  const set = async (s, label) => {
    let reason;
    if (s === "out_of_service") { reason = window.prompt("Reason required for OUT OF SERVICE:"); if (!reason?.trim()) return; }
    if (s === "transporting") { setTModal("transporting"); return; }
    if (s === "at_destination" && (unit?.transport_open || unit?.status === "transporting")) { setTModal("at_destination"); return; }
    if ((offline || !navigator.onLine) && enqueueStatus) { enqueueStatus(s, label); return; }
    await send(s, label, { reason }).catch(() => {});
  };
  return (
    <div className="rounded-lg border border-[#2C2D35] bg-[#141518] p-3 mt-3" data-testid="mdt-status-strip">
      <TransportModal mode={tModal} open={!!tModal} onClose={() => setTModal(null)} openLeg={unit?.transport_open} fromAddress={activeCall?.location?.address || ""} discipline={unit?.type || "police"}
        onSubmit={(extra) => send(tModal, tModal === "transporting" ? "TRANSPORTING" : "AT DESTINATION", extra)} />
      {unit?.transport_open && <div data-testid="mdt-transport-open" className="mb-2 px-2 py-1.5 border border-[#22D3EE]/50 bg-[#22D3EE]/10 font-mono text-[10px] text-[#A5F3FC]">🚑 TRANSPORT IN PROGRESS → <b className="text-white">{unit.transport_open.destination}</b> · start odometer {unit.transport_open.start_mileage} · tap AT DESTINATION to enter ending mileage</div>}
      <div className="font-mono text-[9px] uppercase mb-2" style={{ color: "#6ea8ff" }}>Unit Status — current: <b className="text-white">{(unit?.status || "").replace(/_/g, " ").toUpperCase()}</b></div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
        {EXT_STATUSES.map(([s, label]) => (
          <button key={s} data-testid={`mdt-status-${s}`} disabled={!!busy} onClick={() => set(s, label)}
            className="py-2.5 text-[10px] font-head font-bold rounded-sm border border-[#2C2D35] text-[#C9CDD4] hover:border-[#22D3EE] hover:text-white disabled:opacity-40"
            style={s === unit?.status ? { background: "#22D3EE", color: "#000", borderColor: "#22D3EE" } : {}}>
            {busy === s ? <Loader2 size={11} className="inline animate-spin" /> : label}
          </button>))}
      </div>
    </div>
  );
}

export function MdtSafetyPanel({ myUnit, activeCc }) {
  const [busy, setBusy] = useState("");
  const fire = async (kind, label) => {
    if (!window.confirm(`SEND "${label}"? Dispatch and supervisors are alerted immediately with your location.`)) return;
    setBusy(kind);
    try {
      const loc = await gps();
      await client.post("/safety-report", { kind, unit: myUnit, incident_cc: activeCc, ...loc });
      toast.success(`${label} transmitted — dispatch & supervisors alerted`);
    } catch (e) { toast.error(e.response?.data?.detail || "Transmit failed"); }
    setBusy("");
  };
  return (
    <div className="rounded-lg border border-[#EF4444]/60 bg-[#141518] p-3 mt-3" data-testid="mdt-safety-panel">
      <div className="font-head text-[11px] font-bold tracking-wider text-[#EF4444] mb-2 flex items-center gap-1.5"><ShieldAlert size={12} /> RESPONDER SAFETY</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        {SAFETY.map(([k, label, c]) => (
          <button key={k} data-testid={`mdt-safety-${k}`} disabled={!!busy} onClick={() => fire(k, label)}
            className="py-3 text-[10px] font-head font-bold rounded-sm border disabled:opacity-40" style={{ borderColor: c, color: c }}>
            {busy === k ? <Loader2 size={11} className="inline animate-spin" /> : label}
          </button>))}
      </div>
    </div>
  );
}

export function BoloCenter({ isSupervisor, myUnit }) {
  const [bolos, setBolos] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => client.get("/bolos").then((r) => setBolos(r.data.bolos || [])).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  const act = async (bid, action, note = "", extra = {}) => {
    try { await client.post(`/bolos/${bid}/action`, { action, note, ...extra }); toast.success("Logged"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const publish = async () => {
    try { await client.post("/bolos", form); toast.success("BOLO published"); setForm(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
  };
  const inp = "w-full bg-[#0d0e11] border border-[#2C2D35] px-2 py-1.5 text-[11px] font-mono text-white rounded-sm outline-none mb-1.5";
  return (
    <div className="rounded-lg border border-[#F59E0B]/50 bg-[#141518] p-3 mt-3" data-testid="bolo-center">
      <div className="font-head text-[11px] font-bold tracking-wider text-[#F59E0B] mb-2 flex items-center gap-1.5 flex-wrap">
        <Megaphone size={12} /> BOLO CENTER ({bolos.length} ACTIVE)
        {isSupervisor && <button data-testid="bolo-new" onClick={() => setForm(form ? null : { alert_type: "PERSON", priority: 2, requires_ack: true })}
          className="ml-auto px-2.5 py-1 text-[9px] font-head font-bold bg-[#F59E0B] text-black rounded-sm">{form ? "CANCEL" : "+ PUBLISH BOLO"}</button>}
      </div>
      {form && (
        <div className="border border-[#F59E0B]/40 rounded-sm p-2 mb-2">
          <input data-testid="bolo-person" className={inp} placeholder="Person description" onChange={(e) => setForm({ ...form, person_desc: e.target.value })} />
          <input data-testid="bolo-vehicle" className={inp} placeholder="Vehicle description (plate, make, color…)" onChange={(e) => setForm({ ...form, vehicle_desc: e.target.value })} />
          <input className={inp} placeholder="Last known location" onChange={(e) => setForm({ ...form, last_location: e.target.value })} />
          <div className="grid grid-cols-2 gap-1.5">
            <input className={inp} placeholder="Direction of travel" onChange={(e) => setForm({ ...form, direction: e.target.value })} />
            <input className={inp} placeholder="Weapons warning" onChange={(e) => setForm({ ...form, weapons: e.target.value })} />
          </div>
          <input className={inp} placeholder="Safety instructions" onChange={(e) => setForm({ ...form, instructions: e.target.value })} />
          <input className={inp} placeholder="Expires (YYYY-MM-DD, optional)" onChange={(e) => setForm({ ...form, expires: e.target.value })} />
          <button data-testid="bolo-publish" onClick={publish} className="w-full py-2 text-[10px] font-head font-bold bg-[#F59E0B] text-black rounded-sm">PUBLISH — ACK REQUIRED</button>
        </div>)}
      {bolos.map((b) => {
        const acked = (b.acks || []).some((a) => a.unit === myUnit);
        return (
          <div key={b.id} className="border border-[#2C2D35] rounded-sm p-2 mb-1.5" data-testid={`bolo-${b.number}`}>
            <div className="flex items-center gap-2 flex-wrap font-mono text-[10px]">
              <b className="text-[#F59E0B]">{b.number}</b><span className="text-[#9CA3AF]">{b.alert_type} · P{b.priority}</span>
              <span className="text-[#6B7280]">{b.ts?.slice(0, 16)} · {b.issued_by}</span>
              {b.expires && <span className="text-[#6B7280]">exp {b.expires}</span>}
              {b.weapons && <span className="text-[#EF4444] font-bold"><Siren size={9} className="inline" /> {b.weapons}</span>}
              <span className="ml-auto text-[#10B981]">{(b.acks || []).length} ACK</span>
            </div>
            {b.person_desc && <div className="font-mono text-[11px] text-[#C9CDD4] mt-1">PERSON: {b.person_desc}</div>}
            {b.vehicle_desc && <div className="font-mono text-[11px] text-[#C9CDD4]">VEHICLE: {b.vehicle_desc}</div>}
            {b.last_location && <div className="font-mono text-[10px] text-[#9CA3AF]"><MapPin size={9} className="inline" /> {b.last_location} {b.direction && `→ ${b.direction}`}</div>}
            {b.instructions && <div className="font-mono text-[10px] text-[#F59E0B]">{b.instructions}</div>}
            <div className="flex gap-1.5 mt-1.5 flex-wrap">
              {!acked ? <button data-testid={`bolo-ack-${b.number}`} onClick={() => act(b.id, "ack")} className="px-2.5 py-1 text-[9px] font-head font-bold bg-[#10B981] text-black rounded-sm">ACKNOWLEDGE</button>
                : <span className="px-2 py-1 text-[9px] font-mono text-[#10B981]"><Eye size={9} className="inline" /> ACKNOWLEDGED</span>}
              <button data-testid={`bolo-match-${b.number}`} onClick={async () => { const n = window.prompt("Possible match — describe what you see:"); if (n?.trim()) act(b.id, "match", n.trim(), await gps()); }}
                className="px-2.5 py-1 text-[9px] font-head font-bold border border-[#22D3EE] text-[#22D3EE] rounded-sm">POSSIBLE MATCH</button>
              {isSupervisor && <button onClick={() => { const n = window.prompt("Reason to close/cancel:"); if (n?.trim()) act(b.id, "cancel", n.trim()); }}
                className="px-2.5 py-1 text-[9px] font-head font-bold border border-[#EF4444] text-[#EF4444] rounded-sm">CANCEL</button>}
            </div>
            {(b.tips || []).slice(-3).map((t, i) => <div key={i} className="font-mono text-[9px] text-[#22D3EE] mt-0.5">TIP ({t.kind}) {t.unit || t.by}: {t.note}</div>)}
          </div>);
      })}
      {!bolos.length && <div className="font-mono text-[10px] text-[#6B7280]">No active BOLOs.</div>}
    </div>
  );
}
